import React, { useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext.js'
import { ThemeProvider } from './context/ThemeContext.js'
import ProtectedRoute from './components/ProtectedRoute.jsx'
import Sidebar from './components/Sidebar.jsx'
import CheckinToast from './components/CheckinToast.jsx'
import EnvBanner from './components/EnvBanner.jsx'
import Checkins from './pages/Checkins.jsx'
import SafetyRulesEditor from './pages/SafetyRulesEditor.jsx'
import QuizEditor from './pages/QuizEditor.jsx'
import LanguageManagement from './pages/LanguageManagement.jsx'
import TenantManagement from './pages/TenantManagement.jsx'
import RuleLibrary from './pages/RuleLibrary.jsx'
import TenantFeatures from './pages/TenantFeatures.jsx'
import ScreenManager from './pages/ScreenManager.jsx'
import FormFlowBuilder from './pages/FormFlowBuilder.jsx'
import InfoPages from './pages/InfoPages.jsx'
import Translations from './pages/Translations.jsx'
import Settings from './pages/Settings.jsx'
import Documents from './pages/Documents.jsx'
import DailyReport from './pages/DailyReport.jsx'
import WelcomeScreenEditor from './pages/WelcomeScreenEditor.jsx'
import Notifications from './pages/Notifications.jsx'
import SipSettings from './pages/SipSettings.jsx'
import StandbySettings from './pages/StandbySettings.jsx'
import ThemeSettings from './pages/ThemeSettings.jsx'
import UserManagement from './pages/UserManagement.jsx'
import LicensePlates from './pages/LicensePlates.jsx'
import LprEvents from './pages/LprEvents.jsx'
import Destinations from './pages/Destinations.jsx'
import DestinationBoard from './pages/DestinationBoard.jsx'
import Stations from './pages/Stations.jsx'
import Allowlist from './pages/Allowlist.jsx'
import Lieferavis from './pages/Lieferavis.jsx'

const DashboardLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  )

  const toggle = () => setCollapsed(c => {
    const next = !c
    localStorage.setItem('sidebar-collapsed', String(next))
    return next
  })

  return (
    <div className={`admin-layout${collapsed ? ' layout--collapsed' : ''}`}>
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <main className="admin-main">
        <div className="admin-content">
          {children}
        </div>
      </main>
    </div>
  )
}

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <EnvBanner />
        <CheckinToast />
        <Router>
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardLayout><Checkins /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/rules" element={
              <ProtectedRoute adminOnly requiredFeature="safety_rules_enabled">
                <DashboardLayout><SafetyRulesEditor /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/quiz" element={
              <ProtectedRoute adminOnly requiredFeature="allow_quiz">
                <DashboardLayout><QuizEditor /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/languages" element={
              <ProtectedRoute adminOnly requiredFeature="allow_language_management">
                <DashboardLayout><LanguageManagement /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/rule-library" element={
              <ProtectedRoute ownerOnly>
                <DashboardLayout><RuleLibrary /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/tenants" element={
              <ProtectedRoute ownerOnly>
                <DashboardLayout><TenantManagement /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/stations" element={
              <ProtectedRoute ownerOnly>
                <DashboardLayout><Stations /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/tenant-features" element={
              <ProtectedRoute ownerOnly>
                <DashboardLayout><TenantFeatures /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/screen-manager" element={
              <ProtectedRoute ownerOnly>
                <DashboardLayout><ScreenManager /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/info-pages" element={
              <ProtectedRoute adminOnly requiredFeature="info_pages_enabled">
                <DashboardLayout><InfoPages /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/flow-builder" element={
              <ProtectedRoute ownerOnly>
                <DashboardLayout><FormFlowBuilder /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/translations" element={
              <ProtectedRoute adminOnly requiredFeature="translations_enabled">
                <DashboardLayout><Translations /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/notifications" element={
              <ProtectedRoute>
                <DashboardLayout><Notifications /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/sip-settings" element={
              <ProtectedRoute adminOnly requiredFeature="sip_enabled">
                <DashboardLayout><SipSettings /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/standby-settings" element={
              <ProtectedRoute adminOnly requiredFeature="standby_settings_enabled">
                <DashboardLayout><StandbySettings /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/lpr-events" element={
              <ProtectedRoute>
                <DashboardLayout><LprEvents /></DashboardLayout>
              </ProtectedRoute>
            } />

            {/* /live-sessions was merged into /destination-board: both listed the same
                visitors and both offered a way to say where they should go, so the same
                driver appeared twice with two controls for one decision. Redirected
                rather than removed, because the check-in toast and bookmarks point here. */}
            <Route path="/live-sessions" element={<Navigate to="/destination-board" replace />} />

            {/* Configuring destinations IS an admin act, unlike assigning to them. */}
            <Route path="/destinations" element={
              <ProtectedRoute adminOnly requiredFeature="destinations_enabled">
                <DashboardLayout><Destinations /></DashboardLayout>
              </ProtectedRoute>
            } />

            {/* The board is NOT adminOnly — assigning and releasing is reception's
                daily job and includes the 'user' role. No requiredFeature either: it
                also serves tenants that only assign info pages, so gating it on
                destinations would lock out half the tenants it is for. The sidebar
                decides visibility from the two conditions together. */}
            <Route path="/destination-board" element={
              <ProtectedRoute>
                <DashboardLayout><DestinationBoard /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/license-plates" element={
              <ProtectedRoute adminOnly requiredFeature="license_plates_enabled">
                <DashboardLayout><LicensePlates /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/allowlist" element={
              <ProtectedRoute adminOnly requiredFeature="allowlist_enabled">
                <DashboardLayout><Allowlist /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/lieferavis" element={
              <ProtectedRoute adminOnly requiredFeature="deliveries_enabled" featureMustBeOn>
                <DashboardLayout><Lieferavis /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/theme-settings" element={
              <ProtectedRoute adminOnly requiredFeature="theme_settings_enabled">
                <DashboardLayout><ThemeSettings /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/users" element={
              <ProtectedRoute adminOnly requiredFeature="users_enabled">
                <DashboardLayout><UserManagement /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute>
                <DashboardLayout><Settings /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/welcome-screen" element={
              <ProtectedRoute adminOnly requiredFeature="welcome_screen_enabled">
                <DashboardLayout><WelcomeScreenEditor /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/documents" element={
              <ProtectedRoute>
                <DashboardLayout><Documents /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/daily-report" element={
              <ProtectedRoute>
                <DashboardLayout><DailyReport /></DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
